import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePwaResult {
  /** Whether the app can be installed (A2HS) */
  readonly canInstall: boolean;
  /** Whether the app is already running as standalone (installed) */
  readonly isStandalone: boolean;
  /** Whether the SW is registered and active */
  readonly isOfflineReady: boolean;
  /** Trigger the install prompt */
  readonly install: () => Promise<void>;
}

export function usePwa(): UsePwaResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isOfflineReady, setIsOfflineReady] = useState(false);

  useEffect(() => {
    // Check standalone mode
    const mql = window.matchMedia('(display-mode: standalone)');
    setIsStandalone(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    mql.addEventListener('change', handler);

    // Listen for install prompt
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          if (reg.active) setIsOfflineReady(true);
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            newWorker?.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') setIsOfflineReady(true);
            });
          });
        })
        .catch((err) => console.error('SW registration failed:', err));
    }

    return () => {
      mql.removeEventListener('change', handler);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return {
    canInstall: deferredPrompt !== null,
    isStandalone,
    isOfflineReady,
    install,
  };
}
